import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useDate } from '../hooks';
import {
  dateInRange,
  Days,
  getDayOfWeek,
  getDaysInMonth,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getMiddayTimestamp,
  offsetDateByDays,
  SHORT_DAY_NAMES,
  SHORT_MONTH_NAMES,
} from '../../../common/utils/time';
import {
  CalendarContainer,
  CalendarControls,
  CalendarGrid,
  ControlGroup,
  DayCell,
  DayCellContent,
  DayCellDate,
  GroupSpacer,
  HeaderCell,
  MonthCell,
  MonthCellContent,
} from './elements';
import { CalendarPropsType, HoveredDateType } from './types';
import { Button, TextField } from '@mollybet/ui';

export const FIRST_DAY_OF_WEEK = Days.MONDAY;
export const range = (n: number) => Array(n).fill(undefined);

export const Calendar = ({
  DayComponent,
  onDateChange,
  onClick,
}: CalendarPropsType) => {
  const history = useHistory();
  const location = useLocation();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const [hoveredDate, setHoveredDate] = useState<HoveredDateType>();
  const currentDate = useDate();
  const currentDateString = useMemo(
    () => currentDate.toISOString().slice(0, 10),
    [currentDate]
  );
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [weekCount, _setWeekCount] = useState(5);
  const [previousDateRange, setPreviousDateRange] = useState(null);

  const calendarStartDate = useMemo(
    () =>
      getDayOfWeek(
        params.get('date')
          ? getMiddayTimestamp(params.get('date'))
          : offsetDateByDays(currentDate, -((weekCount - 1) * 7)),
        FIRST_DAY_OF_WEEK
      ),
    [params, currentDate, weekCount]
  );

  const [dateRange, setDateRange] = useState<[from: Date, to: Date]>([
    calendarStartDate,
    offsetDateByDays(calendarStartDate, weekCount * 7 - 1),
  ]);

  useEffect(() => {
    const newDateRange: [from: Date, to: Date] = [
      calendarStartDate,
      offsetDateByDays(calendarStartDate, weekCount * 7 - 1),
    ];

    if (isFirstLoad) {
      setIsFirstLoad(false);
      onDateChange(newDateRange, weekCount);
    }
    if (
      +newDateRange[0] !== +dateRange[0] ||
      +newDateRange[1] !== +dateRange[1]
    ) {
      setDateRange(newDateRange);
      onDateChange(newDateRange, weekCount);
    }
    // eslint-disable-next-line
  }, [calendarStartDate, weekCount, isFirstLoad, onDateChange]);

  const monthCells = useMemo(() => {
    const ranges: { month: number; rowStart: number; rowEnd: number }[] = [];

    if (dateRange[0]) {
      const weeks = Math.ceil(
        (getDaysInMonth(dateRange[0]) - dateRange[0].getDate() + 1) / 7
      );
      const rowEnd = Math.min(
        2 * weeks +
          (getLastDayOfMonth(dateRange[0]).getDay() ===
          (FIRST_DAY_OF_WEEK + 6) % 7
            ? 2
            : 1),
        2 * weekCount + 2
      );
      ranges.push({ month: dateRange[0].getMonth(), rowStart: 2, rowEnd });
    }

    if (dateRange[1] && dateRange[0].getMonth() !== dateRange[1].getMonth()) {
      const weeks = weekCount - Math.ceil(dateRange[1].getDate() / 7);
      const rowStart =
        2 * weeks +
        (getFirstDayOfMonth(dateRange[1]).getDay() === FIRST_DAY_OF_WEEK
          ? 2
          : 3);
      ranges.push({
        month: dateRange[1].getMonth(),
        rowStart,
        rowEnd: 2 * weekCount + 2,
      });
    }

    if (
      dateRange[0] &&
      dateRange[1] &&
      ranges[0] &&
      ranges[1] &&
      (dateRange[0].getMonth() + 1) % 12 !== dateRange[1].getMonth()
    ) {
      ranges.push({
        month: dateRange[0].getMonth() + 1,
        rowStart: ranges[0].rowEnd,
        rowEnd: ranges[1].rowStart,
      });
    }

    return ranges.length ? (
      ranges.map((range) => (
        <MonthCell
          key={`${SHORT_MONTH_NAMES[range.month]}`}
          rowStart={range.rowStart}
          rowEnd={range.rowEnd}
        >
          <MonthCellContent>{SHORT_MONTH_NAMES[range.month]}</MonthCellContent>
        </MonthCell>
      ))
    ) : (
      <MonthCell rowStart={2} rowEnd={weekCount + 2} />
    );
  }, [dateRange, weekCount]);

  const goToToday = useCallback(() => {
    setPreviousDateRange(dateRange);
    const params = new URLSearchParams(location.search);
    params.delete('date');
    history.push(`${location.pathname}?${params}`);
  }, [history, location, dateRange]);

  const goTo = useCallback(
    (date?: Date | string | number) => {
      if (!date) goToToday();
      setPreviousDateRange(dateRange);
      date = new Date(date || new Date());
      const params = new URLSearchParams(location.search);
      params.set('date', date.toISOString().slice(0, 10));
      history.push(`${location.pathname}?${params}`);
    },
    [history, location, dateRange, goToToday]
  );

  const setWeekCount = useCallback(
    (count?: number) => {
      if (!count) return;
      setPreviousDateRange(dateRange);
      _setWeekCount(count);
    },
    // eslint-disable-next-line
    [_setWeekCount]
  );

  return (
    <CalendarContainer>
      <CalendarControls>
        <ControlGroup>
          <Button onClick={goToToday}>Today</Button>
          <GroupSpacer />
          <Button onClick={() => goTo(offsetDateByDays(calendarStartDate, -7))}>
            Prev Week
          </Button>
          <Button onClick={() => goTo(offsetDateByDays(calendarStartDate, 7))}>
            Next Week
          </Button>
          <GroupSpacer />
          <TextField
            type="date"
            value={params.get('date') || ''}
            onChange={(event) => goTo(event.currentTarget.value)}
          />
        </ControlGroup>
        <ControlGroup>
          <Button
            variant={weekCount === 1 ? 'primary' : 'secondary'}
            onClick={() => setWeekCount(1)}
          >
            Week
          </Button>
          <Button
            variant={weekCount === 2 ? 'primary' : 'secondary'}
            onClick={() => setWeekCount(2)}
          >
            Fortnight
          </Button>
          <Button
            variant={weekCount === 5 ? 'primary' : 'secondary'}
            onClick={() => setWeekCount(5)}
          >
            Month
          </Button>
        </ControlGroup>
      </CalendarControls>
      <CalendarGrid weeks={weekCount + 1}>
        <div />
        {monthCells}
        {range(7).map((_, dayNo) => {
          const weekDayNo = (dayNo + FIRST_DAY_OF_WEEK) % 7;
          return (
            <HeaderCell key={`${SHORT_DAY_NAMES[weekDayNo]}-title`}>
              {SHORT_DAY_NAMES[weekDayNo]}
            </HeaderCell>
          );
        })}
        {range(weekCount).map((_, weekNo) =>
          range(7).map((_, dayNo) => {
            const date = offsetDateByDays(
              calendarStartDate,
              weekNo * 7 + dayNo
            );
            const dateString = date.toISOString().slice(0, 10);
            return (
              <DayCell
                key={dateString}
                weekNo={weekNo}
                dayNo={dayNo}
                onMouseEnter={() =>
                  setHoveredDate({
                    date,
                    dateString,
                    weekNo,
                    dayNo,
                  })
                }
                onMouseLeave={() => setHoveredDate(null)}
                onClick={() =>
                  onClick?.({
                    date,
                    dateString,
                    weekNo,
                    dayNo,
                  })
                }
              >
                <DayCellContent animate={!dateInRange(date, previousDateRange)}>
                  <DayCellDate
                    isToday={currentDateString === dateString}
                    isFuture={currentDateString < dateString}
                  >
                    {date.getDate()}
                  </DayCellDate>
                  <DayComponent
                    date={date}
                    dateString={dateString}
                    weekNo={weekNo}
                    dayNo={dayNo}
                    hoveredDate={hoveredDate}
                  />
                </DayCellContent>
              </DayCell>
            );
          })
        )}
      </CalendarGrid>
    </CalendarContainer>
  );
};
